import React, { useEffect, useState } from 'react';
import { ALL_BALLS, BALL_CSS, fmt, lighten } from '@shootout-comps/core';
import type { UseMatchPlayResult } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';
import type { Translations } from '@shootout-comps/i18n';
import type { Player, BallColour, FoulType } from '@shootout-comps/types';
import { BALL_VALUES } from '@shootout-comps/types';
import { Av, SpectatorView, FrameProgressBar, FrameBreakdownList } from './SpectatorMatchView';
import ConnectionStatusBanner from './ConnectionStatusBanner';
import { SnookerBallSpinner } from './SnookerBall';

export interface MatchScoringSurfaceProps {
  mp: UseMatchPlayResult;
  t: Translations;
  backLabel: string;
  onBack: () => void;
  isStandard: boolean;
  spectatorFmt: string;

  spectatorMode: boolean;
  setSpectatorMode: (v: boolean) => void;
  widescreenMode: boolean;
  setWidescreenMode: (v: boolean) => void;
  forcedSpectator: boolean;

  // Sound mute toggle — meaningless without a match clock, so it only ever renders when
  // !isStandard (never for league matches, which are always isStandard). Audio side effects
  // (silenceAll) stay owned by whichever page has the AudioAdapter.
  soundsMuted?: boolean;
  onToggleMute?: (muted: boolean) => void;

  // Tournament-wide "pause every live match" — leagues have no cross-fixture pause concept,
  // so this only renders when the caller supplies onPauseAll.
  isSuperuser?: boolean;
  allPaused?: boolean;
  pausingAll?: boolean;
  onPauseAll?: () => void;

  // Superuser debug tool (jump match clock to 5:30 remaining) — tournament-only, gated on
  // !isStandard the same way it always has been.
  debugJumping?: boolean;
  onDebugJump?: () => void;

  // Explicit opt-in since it isn't naturally gated by isStandard — leagues don't currently
  // expose a restart action, tournaments do.
  showRestartButton?: boolean;

  // Tournament always showed this note regardless of format; league never did — preserved
  // as an explicit flag rather than inferred from isStandard so this refactor changes no
  // page's existing copy.
  showMatchClockNote?: boolean;
}

// Shared presentational scoring surface for both a tournament match and a league match —
// both are scored through the identical /api/matches/:matchId/* engine (see
// apps/api/src/match-parent.ts), so the render tree only needs to vary by these explicit props,
// not by two near-duplicate page components. MatchPlayPage.tsx (tournament) and
// FixtureFramePage.tsx (league) each still call their own useMatchPlay(...) — their setup
// options genuinely differ — and own whatever page-local state feeds these props, then render
// this component with the result.
export default function MatchScoringSurface({
  mp, t, backLabel, onBack, isStandard, spectatorFmt,
  spectatorMode, setSpectatorMode, widescreenMode, setWidescreenMode, forcedSpectator,
  soundsMuted, onToggleMute,
  isSuperuser, allPaused, pausingAll, onPauseAll,
  debugJumping, onDebugJump,
  showRestartButton,
  showMatchClockNote,
}: MatchScoringSurfaceProps) {
  // Free-ball checkbox on the "cue ball in hand" overlay — reset every time a fresh foul
  // brings that overlay back up, so a previous foul's choice never silently carries over.
  const [freeBallChecked, setFreeBallChecked] = useState(false);
  useEffect(() => { if (mp.foulPending) setFreeBallChecked(false); }, [mp.foulPending]);

  const [showRestartConfirm, setShowRestartConfirm] = useState(false);

  // ── Keyboard shortcuts: 1–7 pot the corresponding ball ───────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (!mp.inProgress || !!mp.foulPending || mp.submitting || mp.clockPaused || mp.shotClockPaused) return;
      const idx = parseInt(e.key, 10) - 1; // '1'→0 (red) … '7'→6 (black)
      if (!Number.isInteger(idx) || idx < 0 || idx > 6) return;
      mp.handleShot(ALL_BALLS[idx]);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mp.inProgress, mp.foulPending, mp.submitting, mp.clockPaused, mp.shotClockPaused, mp.handleShot]);

  const pauseAndBack = () => mp.pauseAndBack(onBack);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (!mp.match) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100dvh - var(--header-h))' }}>
      <div style={{ textAlign: 'center', color: 'var(--chalk-dim)' }}>
        <SnookerBallSpinner size={36} />
        <p style={{ marginTop: '0.5rem' }}>Loading…</p>
      </div>
    </div>
  );

  const match = mp.match;
  const ls    = mp.ls;
  const p1    = mp.p1 as Player;
  const p2    = mp.p2 as Player;
  const {
    notStarted, inProgress, breakOffPending, complete, winner, curPlayer,
    displayElapsed, remaining, inSecondHalf, shotLimit, shotLeft, shotWarn, shotOver,
    legalBalls, blackTiedRespot, p1Break, p2Break, p1HighBreak, p2HighBreak, p1AvgShotTime, p2AvgShotTime,
    preMatchHandicapStart1, preMatchHandicapStart2, hasHandicap, canUndo,
    matchDuration, halfTime, foulMode, submitting, showEnd, finishScore1, setFinishScore1,
    finishScore2, setFinishScore2, finishHighBreak1, setFinishHighBreak1, finishHighBreak2, setFinishHighBreak2,
    toast, undoing, flash, clockPaused, foulPending, foulValuePicking,
    setFoulValuePicking, shotClockPaused, restarting,
  } = mp;

  const showDebugJump = !isStandard && !!isSuperuser && halfTime > 0 && !!onDebugJump;

  // Shared between both finish dialogs below — auto-filled from tracked shots when the dialog
  // opens (see openFinishDialog), always freely editable so the organiser can correct it or
  // enter it by hand when no shots were tracked at all.
  const highBreakRow = (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.6rem' }}>
      <div style={{ flex: 1, textAlign: 'center' }}>
        <div style={{ fontSize: '0.65rem', color: 'var(--chalk-dim)', marginBottom: '0.2rem' }}>{t.statsHighBreakLabel}</div>
        <input type="number" min={0} inputMode="numeric" value={finishHighBreak1} onChange={e => setFinishHighBreak1(e.target.value)} disabled={submitting}
          style={{ width: '100%', textAlign: 'center', padding: '0.4rem', fontSize: '0.9rem', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(0,0,0,0.25)', color: 'var(--chalk)' }} />
      </div>
      <div style={{ width: '20px' }} />
      <div style={{ flex: 1, textAlign: 'center' }}>
        <div style={{ fontSize: '0.65rem', color: 'var(--chalk-dim)', marginBottom: '0.2rem' }}>{t.statsHighBreakLabel}</div>
        <input type="number" min={0} inputMode="numeric" value={finishHighBreak2} onChange={e => setFinishHighBreak2(e.target.value)} disabled={submitting}
          style={{ width: '100%', textAlign: 'center', padding: '0.4rem', fontSize: '0.9rem', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(0,0,0,0.25)', color: 'var(--chalk)' }} />
      </div>
    </div>
  );

  // ── Complete screen ───────────────────────────────────────────────────────
  if (complete && winner) return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: 'var(--page-pad)', textAlign: 'center' }}>
      <button onClick={pauseAndBack} style={backBtn}>{backLabel}</button>
      <div style={{ marginTop: '1.5rem' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🏆</div>
        <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(2rem,7vw,3rem)', color: 'var(--gold)', lineHeight: 1 }}>{t.matchComplete}</h2>
        {mp.isMultiFrame && (
          <FrameProgressBar bestOf={mp.bestOf} framesToWin={mp.framesToWin} frameWins1={mp.frameWins1} frameWins2={mp.frameWins2} currentFrame={mp.currentFrame} complete p1={p1} p2={p2} />
        )}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 'clamp(1rem,5vw,3rem)', margin: '1.5rem 0', alignItems: 'center' }}>
          <ScorePanel player={p1} score={mp.isMultiFrame ? mp.frameWins1 : (ls?.score1 ?? match.score1 ?? 0)} isWinner={winner._id === p1._id} highBreak={mp.isMultiFrame ? mp.p1MatchHighBreak : p1HighBreak} avgShotTime={p1AvgShotTime} />
          <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: 'var(--chalk-dim)' }}>{t.vsLabel}</span>
          <ScorePanel player={p2} score={mp.isMultiFrame ? mp.frameWins2 : (ls?.score2 ?? match.score2 ?? 0)} isWinner={winner._id === p2._id} highBreak={mp.isMultiFrame ? mp.p2MatchHighBreak : p2HighBreak} avgShotTime={p2AvgShotTime} />
        </div>
        <p style={{ color: 'var(--gold)', fontFamily: 'Bebas Neue', fontSize: 'clamp(1.4rem,5vw,1.8rem)' }}>{winner.name} {t.wins}</p>
        {ls && <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem', marginTop: '0.5rem' }}>{ls.shots.length} {t.shotsCount} · {fmt(ls.elapsedSeconds ?? mp.elapsed)} {t.elapsed}</p>}
        {mp.isMultiFrame && <FrameBreakdownList frames={mp.frames} p1={p1} p2={p2} />}
      </div>
    </div>
  );

  // ── Main ──────────────────────────────────────────────────────────────────
  return (
    <div style={{
      maxWidth: spectatorMode && widescreenMode ? '100%' : '720px',
      margin: '0 auto',
      padding: spectatorMode && widescreenMode ? '0.5rem 1.5rem' : 'var(--page-pad)',
      position: 'relative',
      transition: 'max-width 0.3s ease, padding 0.3s ease',
    }}>
      <ConnectionStatusBanner />

      {/* Match clock expired overlay — driven purely by mp.flash, which the hook never sets
          to 'match-expired' when isStandard (no match clock exists to expire), so this is
          safe to render unconditionally. */}
      {flash === 'match-expired' && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'flashRed 0.5s ease-in-out infinite alternate',
          pointerEvents: 'none',
          padding: '1rem',
        }}>
          <div style={{
            background: 'rgba(192,20,20,0.93)', borderRadius: '16px',
            padding: 'clamp(1.5rem,5vw,2.5rem)', textAlign: 'center',
            width: '100%', maxWidth: '440px',
            border: '2px solid rgba(255,80,80,0.6)', boxShadow: '0 0 60px rgba(192,20,20,0.8)',
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.6rem' }}>🚨</div>
            <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.6rem,6vw,2.5rem)', color: '#fff', lineHeight: 1 }}>
              {t.matchClockExpired}
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 'clamp(0.85rem,3vw,1rem)', marginTop: '0.5rem' }}>
              {t.endMatchToDeclare}
            </p>
          </div>
        </div>
      )}

      {/* Cue ball in hand overlay — shown after any foul */}
      {foulPending && !spectatorMode && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.75)', padding: '1rem',
        }}>
          <div style={{
            background: 'linear-gradient(135deg, var(--baize), var(--felt))',
            border: '2px solid rgba(201,168,76,0.5)',
            borderRadius: '16px', padding: 'clamp(1.5rem,5vw,2.5rem)',
            textAlign: 'center', width: '100%', maxWidth: '400px',
            boxShadow: '0 16px 48px rgba(0,0,0,0.6)',
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.6rem' }}>🔴</div>
            <h2 style={{
              fontFamily: 'Bebas Neue', fontSize: 'clamp(1.6rem,6vw,2.2rem)',
              color: 'var(--gold)', lineHeight: 1, margin: '0 0 0.5rem',
            }}>
              {t.foulCueBallInHand}
            </h2>
            <p style={{ color: 'var(--chalk-dim)', fontSize: '0.92rem', margin: '0 0 0.35rem', lineHeight: 1.6 }}>
              <strong style={{ color: '#e74c3c' }}>+{foulPending.penalty} points</strong> awarded to the opponent.
            </p>
            <p style={{ color: 'var(--chalk-dim)', fontSize: '0.88rem', margin: '0 0 1.25rem', lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--chalk)' }}>{foulPending.playerName}</strong> {t.toPlayFromHand}
              {t.clocksPausedMsg}
            </p>
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: '0.6rem', textAlign: 'left',
              background: freeBallChecked ? 'rgba(201,168,76,0.1)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${freeBallChecked ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: '10px', padding: '0.75rem 0.9rem', marginBottom: '1.25rem', cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={freeBallChecked}
                onChange={e => setFreeBallChecked(e.target.checked)}
                style={{ marginTop: '0.2rem', flexShrink: 0, width: '16px', height: '16px', accentColor: 'var(--gold)' }}
              />
              <span style={{ fontSize: '0.82rem', color: 'var(--chalk-dim)', lineHeight: 1.5 }}>
                <strong style={{ color: 'var(--chalk)' }}>{t.freeBallLabel}</strong> — {foulPending.playerName} {t.freeBallSnookeredMsg}
              </span>
            </label>
            <button
              onClick={() => mp.handleFoulResume(freeBallChecked)}
              style={{
                background: 'linear-gradient(135deg, var(--gold), var(--gold-light))',
                color: 'var(--baize-dark)', border: 'none', borderRadius: '10px',
                padding: '0.9rem 2.5rem', fontSize: '1rem', fontWeight: 700,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                cursor: 'pointer', boxShadow: '0 4px 16px rgba(201,168,76,0.3)',
                width: '100%', maxWidth: '260px',
              }}
            >
              {t.resumeMatch}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
        <button onClick={pauseAndBack} style={backBtn}>{backLabel}</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {/* Widescreen toggle — desktop only, only visible in spectator mode */}
          {spectatorMode && (
            <label className="widescreen-toggle" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', userSelect: 'none', fontSize: '0.72rem', color: widescreenMode ? '#3498db' : 'var(--chalk-dim)' }}>
              <span style={{ width: '32px', height: '18px', borderRadius: '9px', background: widescreenMode ? '#3498db' : 'rgba(255,255,255,0.15)', display: 'inline-flex', alignItems: 'center', padding: '2px', transition: 'background 0.2s', flexShrink: 0 }}>
                <span style={{ width: '14px', height: '14px', borderRadius: '50%', background: '#fff', transform: widescreenMode ? 'translateX(14px)' : 'translateX(0)', transition: 'transform 0.2s', display: 'block' }} />
              </span>
              ⛶ Wide
              <input type="checkbox" checked={widescreenMode} onChange={e => setWidescreenMode(e.target.checked)} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
            </label>
          )}
          {/* Mute sounds toggle — meaningless without a match clock */}
          {!isStandard && !forcedSpectator && !spectatorMode && onToggleMute && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', cursor: 'pointer', userSelect: 'none', fontSize: '0.72rem', color: soundsMuted ? '#e74c3c' : 'var(--chalk-dim)' }}>
              <span style={{ width: '32px', height: '18px', borderRadius: '9px', background: soundsMuted ? '#e74c3c' : 'rgba(255,255,255,0.15)', display: 'inline-flex', alignItems: 'center', padding: '2px', transition: 'background 0.2s', flexShrink: 0 }}>
                <span style={{ width: '14px', height: '14px', borderRadius: '50%', background: '#fff', transform: soundsMuted ? 'translateX(14px)' : 'translateX(0)', transition: 'transform 0.2s', display: 'block' }} />
              </span>
              {soundsMuted ? '🔇 Muted' : '🔊 Sound'}
              <input type="checkbox" checked={!!soundsMuted} onChange={e => onToggleMute(e.target.checked)} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
            </label>
          )}
          {/* Spectator mode toggle — hidden when this tab was forced into spectator */}
          {!forcedSpectator && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', cursor: 'pointer', userSelect: 'none', fontSize: '0.72rem', color: spectatorMode ? 'var(--gold)' : 'var(--chalk-dim)' }}>
              <span style={{ width: '32px', height: '18px', borderRadius: '9px', background: spectatorMode ? 'var(--gold)' : 'rgba(255,255,255,0.15)', display: 'inline-flex', alignItems: 'center', padding: '2px', transition: 'background 0.2s', flexShrink: 0 }}>
                <span style={{ width: '14px', height: '14px', borderRadius: '50%', background: '#fff', transform: spectatorMode ? 'translateX(14px)' : 'translateX(0)', transition: 'transform 0.2s', display: 'block' }} />
              </span>
              👁 Spectator
              <input type="checkbox" checked={spectatorMode} onChange={e => { setSpectatorMode(e.target.checked); if (!e.target.checked) setWidescreenMode(false); }} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
            </label>
          )}
        </div>
      </div>

      {/* Forced spectator notice */}
      {forcedSpectator && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(52,152,219,0.1)', border: '1px solid rgba(52,152,219,0.3)', borderRadius: '8px', padding: '0.5rem 0.75rem', marginBottom: '0.5rem', fontSize: '0.75rem', color: '#3498db' }}>
          <span>👁</span>
          <span>{t.spectatorView}</span>
        </div>
      )}

      {/* ── Spectator View ── */}
      {spectatorMode && (
        <SpectatorView
          match={match}
          ls={ls}
          p1={p1} p2={p2}
          curPlayer={curPlayer}
          inProgress={inProgress}
          breakOffPending={breakOffPending}
          complete={complete}
          remaining={remaining}
          clockPaused={clockPaused}
          shotLeft={shotLeft}
          shotWarn={shotWarn}
          shotOver={shotOver}
          displayElapsed={displayElapsed}
          matchDuration={matchDuration}
          halfTime={halfTime}
          inSecondHalf={inSecondHalf}
          shotLimit={shotLimit}
          p1Break={p1Break}
          p2Break={p2Break}
          p1HighBreak={p1HighBreak}
          p2HighBreak={p2HighBreak}
          p1AvgShotTime={p1AvgShotTime}
          p2AvgShotTime={p2AvgShotTime}
          p1MatchHighBreak={mp.p1MatchHighBreak}
          p2MatchHighBreak={mp.p2MatchHighBreak}
          bestOf={mp.bestOf}
          framesToWin={mp.framesToWin}
          frameWins1={mp.frameWins1}
          frameWins2={mp.frameWins2}
          currentFrame={mp.currentFrame}
          frames={mp.frames}
          fmt={spectatorFmt}
          isStandard={isStandard}
          widescreen={widescreenMode}
          t={t}
        />
      )}

      {!spectatorMode && (
      <>
      {mp.isMultiFrame && (
        <FrameProgressBar bestOf={mp.bestOf} framesToWin={mp.framesToWin} frameWins1={mp.frameWins1} frameWins2={mp.frameWins2} currentFrame={mp.currentFrame} complete={false} p1={p1} p2={p2} />
      )}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem',
        margin: '1rem 0',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))',
        border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px',
        padding: 'clamp(0.75rem,3vw,1.25rem)',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0 }}>
          <PlayerPanel player={p1} score={ls?.score1 ?? 0} isActive={inProgress && ls?.currentPlayerId === ls?.player1Id} />
          {inProgress && p1Break > 0 && (
            <div style={{ fontSize: '0.6rem', color: 'var(--gold)', fontFamily: 'Bebas Neue', letterSpacing: '0.06em', marginTop: '0.1rem' }}>
              Break: {p1Break}
            </div>
          )}
          {p1HighBreak > 0 && (
            <div style={{ fontSize: '0.55rem', color: 'var(--chalk-dim)', letterSpacing: '0.04em', marginTop: '0.1rem' }}>
              High break: {p1HighBreak}
            </div>
          )}
          {p1AvgShotTime > 0 && (
            <div style={{ fontSize: '0.55rem', color: 'var(--chalk-dim)', letterSpacing: '0.04em', marginTop: '0.1rem' }}>
              Avg shot: {Math.round(p1AvgShotTime)}s
            </div>
          )}
        </div>
        <div style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
          {inProgress && !isStandard && (
            <>
              <div style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.8rem,7vw,2.5rem)', lineHeight: 1, color: remaining <= 60 ? '#e74c3c' : remaining <= 120 ? '#e67e22' : 'var(--chalk)', transition: 'color 0.5s' }}>
                {fmt(Math.max(0, remaining))}
              </div>
              <div style={{ fontSize: '0.6rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{t.remaining}</div>
              <div style={{ marginTop: '0.3rem', fontSize: 'clamp(0.55rem,2vw,0.62rem)', color: inSecondHalf ? '#e67e22' : 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {halfTime === 0
                  ? '20s shot clock'
                  : inSecondHalf ? t.secondHalf : t.firstHalf}
              </div>
            </>
          )}
          {notStarted && <div style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem' }}>{t.notStarted}</div>}
          {complete && <div style={{ color: 'var(--gold)', fontFamily: 'Bebas Neue', fontSize: '1.1rem' }}>{t.complete}</div>}
          {ls && <div style={{ marginTop: '0.3rem', fontSize: '0.65rem', color: 'var(--chalk-dim)' }}>{ls.reds_remaining} {t.redsRemaining}</div>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0 }}>
          <PlayerPanel player={p2} score={ls?.score2 ?? 0} isActive={inProgress && ls?.currentPlayerId === ls?.player2Id} />
          {inProgress && p2Break > 0 && (
            <div style={{ fontSize: '0.6rem', color: 'var(--gold)', fontFamily: 'Bebas Neue', letterSpacing: '0.06em', marginTop: '0.1rem' }}>
              Break: {p2Break}
            </div>
          )}
          {p2HighBreak > 0 && (
            <div style={{ fontSize: '0.55rem', color: 'var(--chalk-dim)', letterSpacing: '0.04em', marginTop: '0.1rem' }}>
              High break: {p2HighBreak}
            </div>
          )}
          {p2AvgShotTime > 0 && (
            <div style={{ fontSize: '0.55rem', color: 'var(--chalk-dim)', letterSpacing: '0.04em', marginTop: '0.1rem' }}>
              Avg shot: {Math.round(p2AvgShotTime)}s
            </div>
          )}
        </div>
      </div>

      {/* Shot clock bar */}
      {inProgress && curPlayer && !breakOffPending && !isStandard && (
        <div style={{
          background: shotOver ? 'rgba(192,57,43,0.2)' : shotWarn ? 'rgba(230,126,34,0.12)' : 'rgba(255,255,255,0.04)',
          border: `1px solid ${shotOver ? 'rgba(192,57,43,0.6)' : shotWarn ? 'rgba(230,126,34,0.4)' : 'rgba(255,255,255,0.08)'}`,
          borderRadius: '10px', padding: '0.65rem 1rem', marginBottom: '0.85rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem',
          animation: shotOver ? 'pulseRed 0.6s ease-in-out infinite alternate' : 'none',
          transition: 'all 0.3s',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minWidth: 0 }}>
            <Av player={curPlayer} size={30} active />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '0.6rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{t.atTheTable}</div>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{curPlayer.name}</div>
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.6rem,6vw,2.2rem)', lineHeight: 1, color: (clockPaused || shotClockPaused) ? '#e67e22' : shotOver ? '#ff4444' : shotWarn ? '#e67e22' : 'var(--chalk)' }}>
              {(clockPaused || shotClockPaused) ? '⏸' : `${shotLeft}s`}
            </div>
            <div style={{ fontSize: '0.58rem', color: (clockPaused || shotClockPaused) ? '#e67e22' : 'var(--chalk-dim)', textTransform: 'uppercase' }}>
              {clockPaused ? 'PAUSED' : shotClockPaused ? 'SHOT ⏸' : `${t.shotClock} (${shotLimit}s)`}
            </div>
          </div>
        </div>
      )}

      {/* Free ball in effect — this shot only */}
      {inProgress && mp.freeBallAvailable && foulMode === 'off' && !breakOffPending && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.55rem 0.85rem', marginBottom: '0.65rem',
          background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: '8px',
          fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold)', letterSpacing: '0.03em',
        }}>
          {t.freeBallActiveBanner}
        </div>
      )}

      {/* Black respotted on a level frame — referee records the toss/lag result */}
      {inProgress && blackTiedRespot && !foulPending && !breakOffPending && (
        <div style={{
          padding: '0.65rem 0.85rem', marginBottom: '0.65rem',
          background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: '10px',
        }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold)', marginBottom: '0.55rem', letterSpacing: '0.02em' }}>
            {t.respotBlackBanner}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {[p1, p2].map(pl => (
              <button
                key={pl._id}
                onClick={() => mp.handleRespotBlack(pl._id)}
                style={{
                  flex: 1, padding: '0.55rem 0.6rem', borderRadius: '8px', cursor: 'pointer',
                  background: ls?.currentPlayerId === pl._id ? 'var(--gold)' : 'rgba(255,255,255,0.06)',
                  color: ls?.currentPlayerId === pl._id ? 'var(--baize-dark)' : 'var(--chalk)',
                  border: '1px solid rgba(201,168,76,0.4)', fontWeight: 600, fontSize: '0.82rem',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
              >
                {pl.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Next ball indicator */}
      {inProgress && ls && foulMode === 'off' && !breakOffPending && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.45rem 0.75rem', marginBottom: '0.65rem', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)', fontSize: '0.75rem', flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--chalk-dim)', flexShrink: 0 }}>{t.nextLegalPot}</span>
          {ALL_BALLS.filter(b => legalBalls.has(b)).map(b => (
            <div key={b} style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
              <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: BALL_CSS[b], border: '1px solid rgba(255,255,255,0.25)', flexShrink: 0 }} />
              <span style={{ color: 'var(--chalk)', fontSize: '0.73rem' }}>{b}</span>
            </div>
          ))}
        </div>
      )}

      {/* Start match */}
      {notStarted && (
        <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
          <p style={{ color: 'var(--chalk-dim)', marginBottom: '1.25rem', fontSize: '0.88rem', lineHeight: 1.6 }}>
            You'll choose who breaks off on the next screen.
            {showMatchClockNote && t.matchClockStartsOnBreak}
          </p>

          {/* Handicap start — shown before match begins, with Start button nested inside */}
          {hasHandicap ? (
            <div style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '10px', padding: '0.85rem 1.25rem', marginBottom: '0', display: 'inline-block', minWidth: '260px' }}>
              <div style={{ fontSize: '0.65rem', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.6rem', fontWeight: 600 }}>
                {t.handicapStart}
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', alignItems: 'center' }}>
                {p1 && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.78rem', color: 'var(--chalk-dim)', marginBottom: '0.2rem', maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p1.name}</div>
                    <div style={{ fontFamily: 'Bebas Neue', fontSize: '2rem', lineHeight: 1, color: preMatchHandicapStart1 > 0 ? 'var(--gold)' : 'var(--chalk-dim)' }}>
                      {preMatchHandicapStart1 > 0 ? `+${preMatchHandicapStart1}` : '0'}
                    </div>
                  </div>
                )}
                <div style={{ color: 'var(--chalk-dim)', fontSize: '0.8rem' }}>vs</div>
                {p2 && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.78rem', color: 'var(--chalk-dim)', marginBottom: '0.2rem', maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p2.name}</div>
                    <div style={{ fontFamily: 'Bebas Neue', fontSize: '2rem', lineHeight: 1, color: preMatchHandicapStart2 > 0 ? 'var(--gold)' : 'var(--chalk-dim)' }}>
                      {preMatchHandicapStart2 > 0 ? `+${preMatchHandicapStart2}` : '0'}
                    </div>
                  </div>
                )}
              </div>
              <div style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)', marginTop: '0.5rem', marginBottom: '1rem' }}>
                Points advantage applied at match start
              </div>
              <button onClick={mp.handleStart} disabled={submitting} style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '10px', padding: '0.9rem 2.5rem', fontSize: '1rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer', boxShadow: '0 4px 20px rgba(201,168,76,0.3)', width: '100%' }}>
                {t.startMatch}
              </button>
            </div>
          ) : (
            <button onClick={mp.handleStart} disabled={submitting} style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '10px', padding: '0.9rem 2.5rem', fontSize: '1rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer', boxShadow: '0 4px 20px rgba(201,168,76,0.3)' }}>
              {t.startMatch}
            </button>
          )}
          {/* Watch as spectator — opens spectator view without starting the match */}
          <button
            onClick={() => setSpectatorMode(true)}
            style={{ marginTop: '0.75rem', background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.55rem 1.5rem', fontSize: '0.82rem', cursor: 'pointer', display: 'block', width: '100%', maxWidth: '260px', marginLeft: 'auto', marginRight: 'auto' }}
          >
            👁 Watch as Spectator
          </button>
        </div>
      )}

      {/* Break Off button — shown after match is started but before break-off is taken */}
      {inProgress && breakOffPending && (
        <BreakOffPanel
          p1={p1}
          p2={p2}
          submitting={submitting}
          onBreakOff={mp.handleBreakOff}
        />
      )}

      {/* Scoring controls */}
      {inProgress && !breakOffPending && (
        <>
          {/* Ball grid */}
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ fontSize: '0.65rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.4rem' }}>
              {foulMode === 'selecting' ? t.selectFoulBall : t.potBall}
            </div>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              {ALL_BALLS.map(colour => {
                const illegal = foulMode === 'off' && !legalBalls.has(colour);
                return (
                  <BallBtn key={colour} colour={colour} disabled={submitting || illegal} illegal={illegal} foulMode={foulMode === 'selecting'}
                    onClick={() => foulMode === 'selecting' ? mp.handleShot(colour, true, 'foul_wrong_ball') : mp.handleShot(colour)} />
                );
              })}
            </div>
          </div>

          {/* Action row */}
          <div style={{ display: 'grid', gridTemplateColumns: isStandard ? 'repeat(3,1fr)' : 'repeat(4,1fr)', gap: '0.4rem', marginBottom: '0.75rem' }}>
            <ActBtn icon="↩" label={t.miss} sub={t.noPot} disabled={submitting || foulMode === 'selecting'} onClick={() => mp.handleShot(null)} color="rgba(255,255,255,0.07)" />
            <ActBtn icon="⚠" label={foulMode === 'selecting' ? t.cancelFoul : t.foul} sub={foulMode === 'selecting' ? t.tapBallAbove : t.selectFoulType} disabled={submitting} active={foulMode === 'selecting'}
              onClick={() => foulMode === 'selecting' ? mp.cancelFoulMode() : mp.enterFoulMode()}
              color="rgba(192,57,43,0.15)" activeColor="rgba(192,57,43,0.3)" />
            <ActBtn icon="⟳" label={t.undo} sub={canUndo && ls ? `#${ls.shots.length}` : t.nothingToUndo} disabled={!canUndo || undoing || submitting} onClick={mp.handleUndo} color="rgba(52,152,219,0.12)" />
            {!isStandard && <ActBtn
              icon={shotClockPaused ? '▶' : '⏸'}
              label={shotClockPaused ? t.resumeShotClock : t.pauseShotClock}
              sub="shot clock"
              disabled={submitting || foulMode === 'selecting' || clockPaused || !inProgress || breakOffPending}
              active={shotClockPaused}
              onClick={shotClockPaused ? mp.handleResumeShotClockOnly : mp.handlePauseShotClockOnly}
              color="rgba(230,126,34,0.12)"
              activeColor="rgba(230,126,34,0.25)"
            />}
          </div>

          {/* Foul sub-options */}
          {foulMode === 'selecting' && (
            <div style={{ background: 'rgba(192,57,43,0.07)', border: '1px solid rgba(192,57,43,0.18)', borderRadius: '10px', padding: '0.6rem', marginBottom: '0.75rem' }}>
              {foulValuePicking ? (
                /* Higher-value ball picker */
                <div>
                  <div style={{ fontSize: '0.68rem', color: 'rgba(231,76,60,0.9)', textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'center', marginBottom: '0.5rem', fontWeight: 600 }}>
                    {t.selectHighestBallTouched}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginBottom: '0.5rem' }}>
                    {([
                      { ball: 'brown' as BallColour, pts: 4 },
                      { ball: 'blue'  as BallColour, pts: 5 },
                      { ball: 'pink'  as BallColour, pts: 6 },
                      { ball: 'black' as BallColour, pts: 7 },
                    ]).map(({ ball, pts }) => (
                      <button
                        key={ball}
                        disabled={submitting}
                        onClick={() => { setFoulValuePicking(false); mp.handleShot(ball, true, 'foul_wrong_ball'); }}
                        style={{
                          width: '58px', height: '58px', borderRadius: '50%',
                          background: BALL_CSS[ball],
                          border: '2px solid rgba(255,255,255,0.25)',
                          cursor: submitting ? 'not-allowed' : 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexDirection: 'column', gap: '1px',
                          boxShadow: '0 3px 10px rgba(0,0,0,0.5)',
                          transition: 'transform 0.1s, box-shadow 0.1s',
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1.1)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
                      >
                        <span style={{ fontWeight: 700, fontSize: '1.1rem', color: ball === 'black' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.8)', lineHeight: 1 }}>{pts}</span>
                        <span style={{ fontSize: '0.48rem', color: ball === 'black' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)', textTransform: 'capitalize', lineHeight: 1 }}>{ball}</span>
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setFoulValuePicking(false)}
                    style={{ width: '100%', background: 'none', border: 'none', color: 'var(--chalk-dim)', fontSize: '0.72rem', cursor: 'pointer', textDecoration: 'underline', padding: '0.2rem' }}
                  >
                    ← Back
                  </button>
                </div>
              ) : (
                /* Standard foul type options */
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '0.35rem' }}>
                  {([
                    { label: t.cueBallPotted, type: 'foul_cue_ball_potted' as FoulType, ball: 'white' as BallColour },
                    { label: t.ballOffTable,  type: 'foul_ball_off_table'  as FoulType, ball: null },
                  ]).map(f => (
                    <button key={f.type} disabled={submitting} onClick={() => mp.handleShot(f.ball, true, f.type)}
                      style={{ background: 'rgba(192,57,43,0.12)', color: '#e74c3c', border: '1px solid rgba(192,57,43,0.25)', borderRadius: '6px', padding: '0.45rem 0.5rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', minHeight: '44px' }}>
                      ⚠ {f.label}
                    </button>
                  ))}
                  <button
                    disabled={submitting}
                    onClick={() => setFoulValuePicking(true)}
                    style={{ background: 'rgba(192,57,43,0.12)', color: '#e74c3c', border: '1px solid rgba(192,57,43,0.25)', borderRadius: '6px', padding: '0.45rem 0.5rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', minHeight: '44px' }}
                  >
                    ⚠ {t.higherValueFoul}
                  </button>
                </div>
              )}
            </div>
          )}

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', margin: '0.75rem 0' }} />

          {/* Pause all timers — tournament-only cross-fixture pause; leagues have no
              cross-fixture concept, so this renders only when the caller supplies onPauseAll. */}
          {!isStandard && onPauseAll && <button
            onClick={onPauseAll}
            disabled={pausingAll}
            style={{ width: '100%', padding: '0.55rem', marginBottom: '0.5rem', background: allPaused ? 'rgba(46,204,113,0.1)' : 'rgba(230,126,34,0.1)', color: allPaused ? '#2ecc71' : '#e67e22', border: `1px solid ${allPaused ? 'rgba(46,204,113,0.35)' : 'rgba(230,126,34,0.3)'}`, borderRadius: '8px', fontSize: '0.8rem', cursor: 'pointer', minHeight: '40px', fontWeight: 600 }}
          >
            {pausingAll ? '…' : allPaused ? '▶ Resume All Clocks' : '⏸ Pause All Clocks'}
          </button>}

          {/* Debug: jump to 5:30 remaining — superuser only, not for standard tournaments */}
          {showDebugJump && (
            <button
              onClick={onDebugJump}
              disabled={debugJumping}
              style={{ width: '100%', padding: '0.45rem', marginTop: '0.35rem', background: 'rgba(155,89,182,0.1)', color: '#9b59b6', border: '1px solid rgba(155,89,182,0.3)', borderRadius: '8px', fontSize: '0.75rem', cursor: 'pointer', minHeight: '36px', fontWeight: 600, letterSpacing: '0.04em' }}
            >
              {debugJumping ? '…' : t.debugJumpBtn}
            </button>
          )}

          {/* Restart match — explicit opt-in, not currently offered for league matches */}
          {showRestartButton && (
            !showRestartConfirm ? (
              <button
                onClick={() => setShowRestartConfirm(true)}
                disabled={restarting}
                style={{ width: '100%', padding: '0.6rem', background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '0.8rem', cursor: 'pointer', minHeight: '44px', marginBottom: '0.4rem' }}
              >
                ↺ Restart Match
              </button>
            ) : (
              <div style={{ background: 'rgba(52,152,219,0.08)', border: '1px solid rgba(52,152,219,0.3)', borderRadius: '10px', padding: '0.85rem', marginBottom: '0.4rem' }}>
                <p style={{ fontSize: '0.8rem', color: 'var(--chalk-dim)', marginBottom: '0.6rem', textAlign: 'center' }}>
                  Restart from the beginning? All shots and scores will be cleared.
                </p>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <button
                    onClick={() => { setShowRestartConfirm(false); mp.handleRestart(); }}
                    disabled={restarting}
                    style={{ flex: 1, padding: '0.5rem', background: 'rgba(52,152,219,0.15)', color: '#5dade2', border: '1px solid rgba(52,152,219,0.4)', borderRadius: '7px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', minHeight: '40px' }}
                  >
                    {restarting ? '…' : '↺ Yes, Restart'}
                  </button>
                  <button
                    onClick={() => setShowRestartConfirm(false)}
                    style={{ flex: 1, padding: '0.5rem', background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '7px', fontSize: '0.82rem', cursor: 'pointer', minHeight: '40px' }}
                  >
                    {t.cancel}
                  </button>
                </div>
              </div>
            )
          )}

          {!showEnd ? (
            <button onClick={mp.openFinishDialog} style={{ width: '100%', padding: '0.6rem', background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '0.8rem', cursor: 'pointer', minHeight: '44px' }}>
              {isStandard ? (mp.isMultiFrame ? t.finishFrameBtn : t.finishMatchBtn) : t.endMatchEarly}
            </button>
          ) : isStandard ? (
            <div style={{ background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.25)', borderRadius: '10px', padding: '0.85rem' }}>
              <p style={{ fontSize: '0.8rem', color: 'var(--chalk-dim)', marginBottom: '0.6rem', textAlign: 'center' }}>{t.enterFinalScore}</p>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.6rem' }}>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)', marginBottom: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p1.name}</div>
                  <input type="number" min={0} inputMode="numeric" value={finishScore1} onChange={e => setFinishScore1(e.target.value)}
                    style={{ width: '100%', textAlign: 'center', padding: '0.5rem', fontSize: '1.1rem', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(0,0,0,0.25)', color: 'var(--chalk)' }} />
                </div>
                <div style={{ color: 'var(--chalk-dim)', fontFamily: 'Bebas Neue', fontSize: '1.2rem', marginTop: '1.1rem' }}>—</div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)', marginBottom: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p2.name}</div>
                  <input type="number" min={0} inputMode="numeric" value={finishScore2} onChange={e => setFinishScore2(e.target.value)}
                    style={{ width: '100%', textAlign: 'center', padding: '0.5rem', fontSize: '1.1rem', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(0,0,0,0.25)', color: 'var(--chalk)' }} />
                </div>
              </div>
              {highBreakRow}
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button onClick={mp.handleFinishStandard} disabled={submitting} style={winBtn}>{mp.isMultiFrame ? t.finishFrameBtn : t.finishMatchBtn}</button>
                <button onClick={() => { mp.setShowEnd(false); setFinishScore1(''); setFinishScore2(''); setFinishHighBreak1(''); setFinishHighBreak2(''); }} style={{ ...winBtn, background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.1)' }}>{t.cancel}</button>
              </div>
            </div>
          ) : (
            <div style={{ background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.25)', borderRadius: '10px', padding: '0.85rem' }}>
              <p style={{ fontSize: '0.8rem', color: 'var(--chalk-dim)', marginBottom: '0.6rem', textAlign: 'center' }}>{t.declareWinner}</p>
              {highBreakRow}
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                {(() => {
                  const score1 = ls?.score1 ?? 0;
                  const score2 = ls?.score2 ?? 0;
                  // A player with a strictly lower score can never be declared the winner —
                  // the backend enforces this too; disabling here just avoids a round-trip 400.
                  const p1CanWin = score1 >= score2;
                  const p2CanWin = score2 >= score1;
                  return (
                    <>
                      <button onClick={() => mp.handleEnd(p1._id)} disabled={submitting || !p1CanWin} style={{ ...winBtn, opacity: (submitting || !p1CanWin) ? 0.4 : 1, cursor: (submitting || !p1CanWin) ? 'not-allowed' : 'pointer' }}>{p1.name} {t.wins}</button>
                      <button onClick={() => mp.handleEnd(p2._id)} disabled={submitting || !p2CanWin} style={{ ...winBtn, opacity: (submitting || !p2CanWin) ? 0.4 : 1, cursor: (submitting || !p2CanWin) ? 'not-allowed' : 'pointer' }}>{p2.name} {t.wins}</button>
                    </>
                  );
                })()}
                <button onClick={() => { mp.setShowEnd(false); setFinishHighBreak1(''); setFinishHighBreak2(''); }} style={{ ...winBtn, background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.1)' }}>{t.cancel}</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Toast — above shot history */}
      {toast && (
        <div style={{
          background: toast.startsWith('❌') ? 'rgba(192,57,43,0.12)' : 'rgba(201,168,76,0.12)',
          border: `1px solid ${toast.startsWith('❌') ? 'rgba(192,57,43,0.3)' : 'rgba(201,168,76,0.3)'}`,
          borderRadius: '8px', padding: '0.55rem 1rem', marginTop: '1rem', marginBottom: '0.5rem',
          fontSize: '0.83rem', color: toast.startsWith('❌') ? '#e74c3c' : 'var(--gold)',
          textAlign: 'center', animation: 'fadeIn 0.2s ease',
        }}>{toast}</div>
      )}

      {/* Shot history */}
      {ls && ls.shots.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <div style={{ fontSize: '0.65rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.4rem' }}>
            {t.shotHistory} ({ls.shots.length})
          </div>
          <div style={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
            {[...ls.shots].reverse().map((shot, i) => {
              const shooter = shot.playerId === ls.player1Id ? p1 : p2;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.73rem', padding: '0.28rem 0.5rem', background: shot.isFoul ? 'rgba(192,57,43,0.08)' : i === 0 ? 'rgba(201,168,76,0.06)' : 'rgba(255,255,255,0.02)', borderRadius: '4px', border: `1px solid ${shot.isFoul ? 'rgba(192,57,43,0.15)' : i === 0 ? 'rgba(201,168,76,0.12)' : 'rgba(255,255,255,0.03)'}` }}>
                  <span style={{ color: 'rgba(255,255,255,0.2)', minWidth: '16px', textAlign: 'right', fontSize: '0.62rem' }}>{ls.shots.length - i}</span>
                  <span style={{ color: 'var(--chalk-dim)', minWidth: '44px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shooter.name.split(' ')[0]}</span>
                  {shot.ballPotted && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: BALL_CSS[shot.ballPotted], border: '1px solid rgba(255,255,255,0.2)', flexShrink: 0 }} />}
                  <span style={{ flex: 1, color: shot.isFoul ? '#e74c3c' : shot.points > 0 ? 'var(--gold)' : 'var(--chalk-dim)' }}>
                    {shot.isFoul ? `${t.foul} +${shot.foulPenalty}` : shot.ballPotted ? `${shot.ballPotted} +${shot.points}${shot.isFreeBall ? ` · 🔴 ${t.freeBallLabel}` : ''}` : t.miss}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      </>
      )}

      <style>{`
        @keyframes flashRed { from { background: rgba(0,0,0,0); } to { background: rgba(192,20,20,0.15); } }
        @keyframes pulseRed { from { opacity: 1; } to { opacity: 0.6; } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        .widescreen-toggle { display: none !important; }
        @media (min-width: 900px) { .widescreen-toggle { display: flex !important; } }
      `}</style>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

export function PlayerPanel({ player, score, isActive }: { player: Player; score: number; isActive: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem', flex: 1, minWidth: 0 }}>
      <Av player={player} size={Math.min(64, 14 * 4)} active={isActive} />
      <div style={{ fontWeight: 600, fontSize: 'clamp(0.7rem,3vw,0.85rem)', maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>{player.name}</div>
      {isActive && <div style={{ fontSize: '0.55rem', color: '#2ecc71', textTransform: 'uppercase', letterSpacing: '0.06em' }}>▶</div>}
      <div style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(2rem,8vw,2.8rem)', lineHeight: 1, color: isActive ? 'var(--gold)' : 'var(--chalk)' }}>{score}</div>
    </div>
  );
}

export function ScorePanel({ player, score, isWinner, highBreak, avgShotTime }: { player: Player; score: number; isWinner: boolean; highBreak?: number; avgShotTime?: number }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <Av player={player} size={64} />
      <div style={{ fontWeight: 600, marginTop: '0.4rem', fontSize: '0.88rem' }}>{player.name}</div>
      <div style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(2rem,8vw,3rem)', color: isWinner ? 'var(--gold)' : 'var(--chalk)' }}>{score}</div>
      {isWinner && <div style={{ color: 'var(--gold)', fontSize: '0.75rem' }}>🏆</div>}
      {!!highBreak && highBreak > 0 && <div style={{ color: 'var(--chalk-dim)', fontSize: '0.72rem', marginTop: '0.2rem' }}>High break: {highBreak}</div>}
      {!!avgShotTime && avgShotTime > 0 && <div style={{ color: 'var(--chalk-dim)', fontSize: '0.72rem', marginTop: '0.15rem' }}>Avg shot: {Math.round(avgShotTime)}s</div>}
    </div>
  );
}

export function BallBtn({ colour, disabled, illegal, foulMode, onClick }: { colour: BallColour; disabled: boolean; illegal: boolean; foulMode: boolean; onClick: () => void }) {
  const sz = 'clamp(40px, 11vw, 50px)';
  return (
    <button onClick={onClick} disabled={disabled}
      title={illegal ? `Wrong sequence` : `${foulMode ? 'Foul: ' : ''}${colour} (+${BALL_VALUES[colour]})`}
      style={{ width: sz, height: sz, borderRadius: '50%', background: `radial-gradient(circle at 35% 35%, ${lighten(BALL_CSS[colour])}, ${BALL_CSS[colour]})`, border: `3px solid ${foulMode ? '#e74c3c' : illegal ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.25)'}`, cursor: disabled ? 'not-allowed' : 'pointer', opacity: illegal ? 0.22 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: illegal ? 'none' : '0 2px 6px rgba(0,0,0,0.4)', transition: 'opacity 0.2s, transform 0.1s', flexShrink: 0 }}
      onMouseDown={e => { if (!disabled) (e.currentTarget as HTMLElement).style.transform = 'scale(0.9)'; }}
      onMouseUp={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
    >
      <span style={{ fontSize: '0.55rem', color: colour === 'black' || colour === 'blue' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.75)', fontWeight: 700 }}>{BALL_VALUES[colour]}</span>
    </button>
  );
}

export function ActBtn({ icon, label, sub, disabled, active, onClick, color, activeColor }: { icon: string; label: string; sub: string; disabled: boolean; active?: boolean; onClick: () => void; color: string; activeColor?: string }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ background: active && activeColor ? activeColor : color, border: `1px solid ${active ? 'rgba(192,57,43,0.45)' : 'rgba(255,255,255,0.09)'}`, borderRadius: '10px', padding: '0.6rem 0.3rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.18rem', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.32 : 1, transition: 'all 0.15s', minHeight: '72px', justifyContent: 'center' }}>
      <span style={{ fontSize: '1.1rem' }}>{icon}</span>
      <span style={{ fontSize: 'clamp(0.62rem,2.5vw,0.72rem)', fontWeight: 700, color: 'var(--chalk)', textAlign: 'center', lineHeight: 1.2 }}>{label}</span>
      <span style={{ fontSize: 'clamp(0.5rem,2vw,0.58rem)', color: 'var(--chalk-dim)', textAlign: 'center', lineHeight: 1.2 }}>{sub}</span>
    </button>
  );
}

export const backBtn: React.CSSProperties = { background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', padding: '0.4rem 0.9rem', fontSize: '0.78rem', cursor: 'pointer', minHeight: '36px' };
export const winBtn:  React.CSSProperties = { flex: 1, padding: '0.55rem', background: 'rgba(201,168,76,0.15)', color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.35)', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', minHeight: '44px', minWidth: '80px' };

// ─── Break-off panel ──────────────────────────────────────────────────────────

export function BreakOffPanel({ p1, p2, submitting, onBreakOff }: {
  p1: Player; p2: Player;
  submitting: boolean;
  onBreakOff: (breakingPlayerId?: string) => void;
}) {
  const { t } = useLang();
  const [selected, setSelected] = useState<string | null>(null);
  const canBreak = selected !== null && !submitting;
  const breakingPlayer = [p1, p2].find(p => String(p._id) === selected);

  return (
    <div style={{ textAlign: 'center', padding: '1.25rem 0 0.5rem' }}>
      <h3 style={{ fontFamily: 'Bebas Neue', fontSize: '1.5rem', color: 'var(--gold)', letterSpacing: '0.06em', margin: '0 0 0.25rem' }}>
        {t.whoBreaks}
      </h3>
      <p style={{ color: 'var(--chalk-dim)', fontSize: '0.78rem', marginBottom: '1.25rem', lineHeight: 1.5 }}>
        {t.whoBreaksDesc}
      </p>

      {/* Player selector */}
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {[p1, p2].map(player => {
          const isSelected = selected === String(player._id);
          return (
            <button
              key={String(player._id)}
              onClick={() => setSelected(String(player._id))}
              style={{
                background: isSelected ? 'rgba(201,168,76,0.18)' : 'rgba(255,255,255,0.04)',
                border: `2px solid ${isSelected ? 'var(--gold)' : 'rgba(255,255,255,0.12)'}`,
                borderRadius: '12px', padding: '0.85rem 1.5rem',
                color: isSelected ? 'var(--gold)' : 'var(--chalk)',
                fontWeight: isSelected ? 700 : 400, fontSize: '0.95rem',
                cursor: 'pointer', transition: 'all 0.15s',
                minWidth: '130px',
              }}
            >
              {isSelected ? '🔴 ' : ''}{player.name}
            </button>
          );
        })}
      </div>

      {/* Break Off button */}
      <button
        onClick={() => canBreak && onBreakOff(selected ?? undefined)}
        disabled={!canBreak}
        style={{
          background: canBreak ? 'linear-gradient(135deg, var(--gold), var(--gold-light))' : 'rgba(255,255,255,0.07)',
          color: canBreak ? 'var(--baize-dark)' : 'var(--chalk-dim)',
          border: 'none', borderRadius: '10px',
          padding: '1rem 3rem', fontSize: '1.1rem', fontWeight: 700,
          letterSpacing: '0.1em', textTransform: 'uppercase',
          cursor: canBreak ? 'pointer' : 'not-allowed',
          boxShadow: canBreak ? '0 4px 24px rgba(201,168,76,0.35)' : 'none',
          opacity: submitting ? 0.6 : 1,
          minHeight: '52px', transition: 'all 0.2s',
        }}
      >
        {submitting ? '…' : breakingPlayer ? `🔴 ${breakingPlayer.name} ${t.breaksOff}` : `🔴 ${t.breakOffBtn}`}
      </button>
      {!selected && (
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.72rem', marginTop: '0.6rem', opacity: 0.7 }}>
          {t.selectPlayerFirst}
        </p>
      )}
    </div>
  );
}
